import {
    CognitoIdToken,
    CognitoUser,
    CognitoUserPool,
    CognitoUserSession,
    CognitoUserAttribute,
} from "amazon-cognito-identity-js";
import { ICognitoDriver } from "./CognitoDriver";
import { LoginResult, ChallengeData } from "../../types/Auth";

export interface IAuthClient {
    login(username: string, password: string): Promise<LoginResult>;
    logout(): void;
    refreshSession(): Promise<void>;
    initializeSession(): Promise<void>;
    getIdentityToken(): CognitoIdToken | null;
    getValidToken(): Promise<string | null>;
    changePassword(oldPassword: string, newPassword: string): Promise<void>;
    getUsername(): string | null;
    getUserAnnotation(): Promise<string | null>;
    completeNewPassword(newPassword: string): Promise<void>;
    getChallengeData(): ChallengeData | null;
    getUserCustomAttribute(attributeName: string): Promise<string | null>;
    isSessionReady(): boolean;
    forgotPassword(username: string): Promise<void>;
    confirmForgotPassword(username: string, code: string, newPassword: string): Promise<void>;
}

/**
 * AuthClient is a stateful wrapper around the ICognitoDriver.
 * AuthClient manages running instances of userPools, users, sessions,
 * and any other metadata needed to perform authorization logic.
 */
export default class AuthClient implements IAuthClient {
    authDriver: ICognitoDriver;
    protected userPool: CognitoUserPool;
    protected session: CognitoUserSession | undefined;
    protected user: CognitoUser | null;
    protected challengeData: ChallengeData | null;
    private refreshTimer: ReturnType<typeof setTimeout> | null = null;
    private sessionInitialized = false;
    private sessionInitPromise: Promise<void>;
    private readonly TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes

    constructor(driver: ICognitoDriver, userPoolId: string, clientId: string) {
        this.authDriver = driver;
        this.challengeData = null;
        const poolData = {
            UserPoolId: userPoolId,
            ClientId: clientId,
        };
        this.userPool = new CognitoUserPool(poolData);
        this.user = this.userPool.getCurrentUser();

        // Initialize the promise that will resolve when session is loaded
        this.sessionInitPromise = this.recoverSession();
    }

    /**
     * Recovers session from localStorage if available.
     * This is called automatically on construction.
     */
    private recoverSession(): Promise<void> {
        return new Promise<void>((resolve) => {
            if (this.user != null) {
                this.user.getSession((err: Error | null, session: CognitoUserSession | null) => {
                    if (!err && session && session.isValid()) {
                        this.session = session;
                        this.scheduleTokenRefresh();
                    }
                    this.sessionInitialized = true;
                    resolve();
                });
            } else {
                this.sessionInitialized = true;
                resolve();
            }
        });
    }

    /**
     * Waits for the session initialization to complete.
     * Call this before rendering protected content to ensure session recovery is done.
     */
    async initializeSession(): Promise<void> {
        return this.sessionInitPromise;
    }

    /**
     * Returns true if the session initialization has completed.
     */
    isSessionReady(): boolean {
        return this.sessionInitialized;
    }

    async login(username: string, password: string): Promise<LoginResult> {
        return new Promise<LoginResult>((resolve, reject) => {
            if (this.session?.isValid()) {
                resolve({
                    success: true,
                    session: this.session,
                });
                return;
            }

            this.user = new CognitoUser({
                Username: username,
                Pool: this.userPool,
            });

            this.authDriver
                .login(this.user, password)
                .then((result) => {
                    if (result.success && result.session) {
                        this.session = result.session;
                        this.challengeData = null;
                        this.scheduleTokenRefresh();
                    } else if (result.challengeData) {
                        this.challengeData = result.challengeData;
                    }
                    resolve(result);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    logout() {
        this.clearTokenRefreshTimer();
        if (this.user) {
            this.authDriver.logout(this.user);
        }
        this.user = null;
        this.session = undefined;
        this.challengeData = null;
    }

    async refreshSession(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (this.session && this.user) {
                this.authDriver
                    .refreshSession(this.user, this.session.getRefreshToken())
                    .then((newSession) => {
                        this.session = newSession;
                        this.scheduleTokenRefresh();
                        resolve(undefined);
                    })
                    .catch((error) => {
                        reject(error);
                    });
                return;
            }

            reject(new Error("cannot refresh session"));
        });
    }

    getIdentityToken(): CognitoIdToken | null {
        if (this.session) {
            return this.session.getIdToken();
        }
        return null;
    }

    async changePassword(oldPassword: string, newPassword: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (this.user) {
                this.authDriver
                    .changePassword(this.user, oldPassword, newPassword)
                    .then(() => {
                        resolve(undefined);
                    })
                    .catch((error) => {
                        reject(error);
                    });
                return;
            }

            reject(new Error("not logged in"));
        });
    }

    getUsername(): string | null {
        if (this.user) {
            return this.user.getUsername();
        }
        return null;
    }

    async getUserAnnotation(): Promise<string | null> {
        return this.getUserAttribute("custom:annotation");
    }

    async completeNewPassword(newPassword: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (!this.challengeData || !this.challengeData.user) {
                reject(new Error("No active challenge"));
                return;
            }

            const user = this.challengeData.user;
            const challengeSession = this.challengeData.session;
            this.authDriver
                .completeNewPassword(user, newPassword, challengeSession)
                .then((session) => {
                    this.session = session;
                    this.user = user;
                    this.challengeData = null;
                    resolve(undefined);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    getChallengeData(): ChallengeData | null {
        return this.challengeData;
    }

    async forgotPassword(username: string): Promise<void> {
        const user = new CognitoUser({
            Username: username,
            Pool: this.userPool,
        });
        return this.authDriver.forgotPassword(user);
    }

    async confirmForgotPassword(username: string, code: string, newPassword: string): Promise<void> {
        const user = new CognitoUser({
            Username: username,
            Pool: this.userPool,
        });
        return this.authDriver.confirmForgotPassword(user, code, newPassword);
    }

    /**
     * Schedules an automatic token refresh before expiration.
     * Clears any existing timer and sets a new one to refresh 5 minutes before expiration.
     */
    private scheduleTokenRefresh(): void {
        this.clearTokenRefreshTimer();
        if (!this.session) {
            return;
        }
        const expirationTime = this.session.getIdToken().getExpiration() * 1000; // Convert to milliseconds
        const refreshIn = expirationTime - Date.now() - this.TOKEN_REFRESH_BUFFER_MS;

        if (refreshIn > 0) {
            this.refreshTimer = setTimeout(() => {
                this.refreshSession()
                    .then(() => {
                        // Token refresh successful
                    })
                    .catch((error) => {
                        console.warn("Automatic token refresh failed:", error);
                    });
            }, refreshIn);
        }
    }

    /**
     * Clears the token refresh timer if one exists.
     */
    private clearTokenRefreshTimer(): void {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
    }

    /**
     * Gets a valid identity token, automatically refreshing if needed.
     * Refreshes the token if it expires within the 5-minute buffer window.
     *
     * @returns the identity token as a JWT string, or null if no valid session exists
     */
    async getValidToken(): Promise<string | null> {
        if (!this.session) {
            return null;
        }

        const expirationTime = this.session.getIdToken().getExpiration() * 1000; // Convert to milliseconds
        const timeUntilExpiration = expirationTime - Date.now();

        // If token expires within buffer, refresh it
        if (timeUntilExpiration < this.TOKEN_REFRESH_BUFFER_MS) {
            try {
                await this.refreshSession();
            } catch (error) {
                console.warn("Failed to refresh token:", error);
                return null;
            }
        }

        return this.session.getIdToken().getJwtToken();
    }

    /**
     * Fetches a custom attribute from Cognito user attributes.
     * @param attributeName The name of the custom attribute (e.g. "custom:video_modal")
     */
    async getUserCustomAttribute(attributeName: string): Promise<string | null> {
        return this.getUserAttribute(attributeName);
    }

    /**
     * Helper method to fetch any user attribute from Cognito.
     * @param attributeName The name of the attribute to fetch
     */
    private async getUserAttribute(attributeName: string): Promise<string | null> {
        if (!this.user) {
            return null;
        }

        return new Promise<string | null>((resolve, reject) => {
            this.user?.getSession((err: Error | null, session: CognitoUserSession | null) => {
                if (err || !session || !session.isValid()) {
                    reject(new Error("invalid or expired session"));
                    return;
                }
                this.user?.getUserAttributes(
                    (err: Error | undefined, attributes: CognitoUserAttribute[] | undefined) => {
                        if (err) {
                            reject(new Error("Error getting user attributes"));
                            return;
                        }

                        if (!attributes) {
                            resolve(null);
                            return;
                        }

                        const attribute = attributes.find((attr) => attr.getName() === attributeName);

                        if (attribute) {
                            resolve(attribute.getValue());
                        } else {
                            resolve(null);
                        }
                    },
                );
            });
        });
    }
}
